import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageMeta } from "@/lib/site";
import { favoritesApi } from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { Empty } from "@/components/partner/PartnerShell";

export default function Favourites() {
  usePageMeta("Favourites", "Listings you've saved on Fable +.");
  const [items, setItems] = useState(undefined);

  useEffect(() => {
    favoritesApi.list().then((d) => setItems(d.listings || [])).catch(() => setItems(null));
  }, []);

  const remove = async (id) => {
    const prev = items;
    setItems((xs) => xs.filter((x) => x.id !== id));
    try {
      await favoritesApi.toggle(id);
    } catch (e) {
      setItems(prev);
      toast.error(e.message || "Couldn't update that.");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
      <h1 className="font-serif-display text-3xl font-bold">Favourites</h1>
      <p className="mt-1 text-muted-foreground">Tap the heart on any listing and it lands here, on the web and in the app.</p>
      <div className="mt-6" data-testid="favourites">
        {items === undefined ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}</div>
        ) : items === null ? (
          <p className="py-10 text-center text-muted-foreground">Couldn't load your favourites.</p>
        ) : items.length === 0 ? (
          <Empty title="Nothing saved yet" body="Save the places and pros you like and compare them later." action={<Link to="/marketplace"><Button className="rounded-full">Browse the marketplace</Button></Link>} />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {items.map((l) => (
              <div key={l.id} className="relative">
                <ListingCard listing={l} />
                <button type="button" onClick={() => remove(l.id)} aria-label="Remove from favourites" data-testid={`unfav-${l.id}`} className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-rose-600 shadow-sm transition-transform hover:scale-105 active:scale-95">
                  <Heart size={16} fill="currentColor" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
